import React, { useMemo } from "react";
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
  FaShareAlt,
} from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import { bureauInfo } from "../../../types";
import { UPLOAD_URL } from "../../../utils/constants";
import { getBureauWebsiteUrl } from "../../../utils/bureau";
import { shareViaNative } from "../../../helpers/nativeShare";
import { toast } from "react-toastify";

type FooterProps = {
  bureauInfo?: bureauInfo;
  navbarLogo?: string;
};

const sanitizePhone = (value?: string) => (value || "").replace(/[^0-9]/g, "");
const formatWhatsappPhone = (value?: string) => {
  const digits = sanitizePhone(value);
  if (!digits) return "";
  // WhatsApp requires country code, no "+" or leading zeros.
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const safeUrl = (url?: string) => {
  if (!url) return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  // allow https/http only; if user saved "www.x.com" add https
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  // if it's something else, don't open (avoid bad schemes)
  return "";
};

const Footer: React.FC<FooterProps> = ({ bureauInfo, navbarLogo }) => {
  const phone = useMemo(
    () => formatWhatsappPhone(bureauInfo?.mobileNumber),
    [bureauInfo?.mobileNumber]
  );

  const socials = useMemo(
    () =>
      [
        {
          key: "facebook",
          url: safeUrl(bureauInfo?.facebook),
          Icon: FaFacebook,
          label: "Facebook",
        },
        {
          key: "twitter",
          url: safeUrl(bureauInfo?.twitter),
          Icon: FaTwitter,
          label: "Twitter / X",
        },
        {
          key: "instagram",
          url: safeUrl(bureauInfo?.instagram),
          Icon: FaInstagram,
          label: "Instagram",
        },
        {
          key: "linkedin",
          url: safeUrl(bureauInfo?.linkedin),
          Icon: FaLinkedin,
          label: "LinkedIn",
        },
        {
          key: "youtube",
          url: safeUrl(bureauInfo?.youtube),
          Icon: FaYoutube,
          label: "YouTube",
        },
      ].filter((s) => Boolean(s.url)),
    [
      bureauInfo?.facebook,
      bureauInfo?.twitter,
      bureauInfo?.instagram,
      bureauInfo?.linkedin,
      bureauInfo?.youtube,
    ]
  );

  const shareSite = async () => {
    const title = bureauInfo?.bureauName || "Matrimony Bureau";
    const url = getBureauWebsiteUrl(bureauInfo);
    if (!url) {
      toast.error("Website details are not available yet.");
      return;
    }
    const about = bureauInfo?.about?.trim();
    const text = [about, `Website: ${url}`].filter(Boolean).join("\n\n");

    try {
      await shareViaNative({ title, text });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(text);
        toast.info("Website details copied.");
      } catch {
        toast.error("Unable to share website.");
      }
    }
  };

  return (
    <>
      {/* FLOATING ACTION BAR */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 shadow-lg z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-3 pb-[calc(0.75rem+var(--bottom-safe-area))] flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={`${UPLOAD_URL}/bureaus/navbar_logos/${navbarLogo}`}
              alt="Bureau logo"
              className="w-8 h-8 object-contain bg-white rounded-xl"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png";
              }}
            />

            <div className="hidden sm:block min-w-0">
              <p className="font-semibold text-slate-800 text-sm truncate">
                {bureauInfo?.bureauName || "Matrimony Bureau"}
              </p>
              <p className="text-xs text-slate-500">Professional Matchmaking</p>
            </div>
          </div>

          <div className="flex gap-3">
            {phone && (
              <a
                href={`https://wa.me/${phone}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full flex items-center justify-center bg-[#25D366] text-white hover:bg-[#1ebe5d] transition"
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
              </a>
            )}

            <button
              onClick={shareSite}
              className="w-11 h-11 rounded-full flex items-center justify-center bg-pink-600 text-white hover:bg-pink-700 transition"
              aria-label="Share"
              type="button"
            >
              <FaShareAlt />
            </button>
          </div>
        </div>
      </div>

      {/* ABOUT US (optional) */}
      {bureauInfo?.about && (
        <section className="py-8 sm:py-14 bg-white border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-semibold text-slate-900 mb-4">
              About Us
            </h2>
            <p className="text-slate-600 leading-relaxed">{bureauInfo.about}</p>
          </div>
        </section>
      )}

      {/* MAIN FOOTER (premium like screenshot) */}
      <footer className="bg-gradient-to-b from-[#1b1c1f] to-[#111214] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <div className="grid gap-10 md:grid-cols-4">
            {/* BRAND */}
            <div className="md:col-span-1">
              <div className="items-center">
                <img
                  src={`${UPLOAD_URL}/bureaus/navbar_logos/${navbarLogo}`}
                  alt="Bureau logo"
                  className="w-full object-contain bg-white rounded-xl"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png";
                  }}
                />
              </div>

              <p className="mt-6 text-sm leading-7 text-white/55">
                Helping thousands of families find their perfect match with
                trusted, verified and personalized matchmaking services.
              </p>

              {/* Social icon buttons (like screenshot) */}
              {socials.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3">
                  {/* Small Social Icons */}
                  {socials.length > 0 && (
                    <div className="mt-6 flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide">
                      {socials.map(({ key, url, Icon, label }) => (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          title={label}
                          className="
          w-9 h-9 shrink-0
          rounded-lg
          bg-white/10 border border-white/10
          flex items-center justify-center
          text-white/70 hover:text-white
          hover:bg-white/15
          transition
          text-sm
        "
                        >
                          <Icon size={14} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* QUICK LINKS */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide text-white mb-4">
                Quick Links
              </h4>
              <ul className="space-y-3 text-sm text-white/55">
                <li>
                  <a href="#" className="hover:text-white transition">
                    Home
                  </a>
                </li>
                <li>
                  <a
                    href="#male-profiles"
                    className="hover:text-white transition"
                  >
                    Male Profiles
                  </a>
                </li>
                <li>
                  <a
                    href="#female-profiles"
                    className="hover:text-white transition"
                  >
                    Female Profiles
                  </a>
                </li>
                <li>
                  <a
                    href="#success-stories"
                    className="hover:text-white transition"
                  >
                    Success Stories
                  </a>
                </li>
                <li>
                  <a
                    href="#packages-section"
                    className="hover:text-white transition"
                  >
                    Packages
                  </a>
                </li>
              </ul>
            </div>

            {/* CONTACT */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide text-white mb-4">
                Contact
              </h4>

              <div className="space-y-3 text-sm text-white/55">
                {bureauInfo?.email && (
                  <a
                    href={`mailto:${bureauInfo.email}`}
                    className="block hover:text-white transition break-words"
                  >
                    {bureauInfo.email}
                  </a>
                )}

                {bureauInfo?.mobileNumber && (
                  <a
                    href={`tel:${bureauInfo.mobileNumber}`}
                    className="block hover:text-white transition"
                  >
                    {bureauInfo.mobileNumber}
                  </a>
                )}

                {phone && (
                  <a
                    href={`https://wa.me/${phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:text-white transition"
                  >
                    WhatsApp
                  </a>
                )}

                {(bureauInfo?.location || bureauInfo?.city) && (
                  <div className="flex gap-2 items-start">
                    <MdLocationOn className="mt-0.5 text-white/60" />
                    <span className="break-words">
                      {bureauInfo?.location || bureauInfo?.city}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* FOLLOW US (text list like screenshot) */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide text-white mb-4">
                Follow Us
              </h4>

              <ul className="space-y-3 text-sm text-white/55">
                {socials.map(({ key, url, label }) => (
                  <li key={`follow-${key}`}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white transition"
                    >
                      {label}
                    </a>
                  </li>
                ))}

                {socials.length === 0 && (
                  <li className="text-white/35">No social links added</li>
                )}
              </ul>
            </div>
          </div>

          {/* Bottom row like screenshot */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col gap-4">
            <p className="text-xs text-white/45">
              © {new Date().getFullYear()}{" "}
              {bureauInfo?.bureauName || "MeeMarriage"} · All rights reserved
            </p>

            <div className="hidden text-xs text-white/45 flex flex-wrap gap-x-3 gap-y-2">
              <a href="/privacy-policy" className="hover:text-white transition">
                Privacy Policy
              </a>
              <span className="text-white/20">|</span>
              <a
                href="/community/guidelines"
                className="hover:text-white transition"
              >
                Terms of Service
              </a>
            </div>

            <div className="space-y-1 text-xs text-white/50">
              <p>© 2026 Riya Tech Solutions.</p>
              <p>All Rights Reserved.</p>
              <p>
                Unauthorized copying of this website, design, images,
                software, database or source code is strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
